import { formatDate, moduleTypeToLabel } from '@/lib/utils';
import {
  REVIEW_CHANGE_BADGE_CONFLICT,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT,
  REVIEW_CHANGE_BADGE_CONFIG,
} from '@/lib/constants/manuscripts/store';

const CONTENT_NOT_PREVIEWABLE = 'This content type isn\'t previewable yet.';

// A badge the backend sends that isn't in this config is dropped rather than guessed at.
const KNOWN_BADGES = new Set(Object.keys(REVIEW_CHANGE_BADGE_CONFIG));

function sideTime(side) {
  return side?.updated_on ? formatDate(side.updated_on, true) : null;
}

// manuscript's own Page.number vs. Studio's 1-indexed sort_index rank -- can differ after a reorder.
function slideLabel(side) {
  return Number.isFinite(side?.order) ? `Slide ${side.order}` : null;
}

function hasText(paragraphs) {
  return paragraphs.some((paragraph) => paragraph.some((segment) => segment.text));
}

function changedParagraphs(textDiff) {
  return (textDiff?.paragraphs ?? []).filter((paragraph) => paragraph.op !== 'equal');
}

// text_diff's delete/insert ops are relative to manuscript -> studio, not to
// which side is "edited". For editedSide === 'studio', delete reads as
// removed and insert as added (the edit happened in Studio); for
// editedSide === 'manuscript' the meaning flips (delete is what manuscript
// added, insert is what manuscript removed). The other side shows plain text.
function buildEditedSides(item, editedSide) {
  const paragraphs = changedParagraphs(item.text_diff);
  const highlightByOp = editedSide === 'manuscript'
    ? { delete: 'added', insert: 'removed' }
    : { delete: 'removed', insert: 'added' };
  const editedParagraphs = paragraphs.map((paragraph) => (paragraph.segments ?? []).map((segment) => ({
    text: segment.text,
    highlight: highlightByOp[segment.op] ?? null,
  })));
  const plainText = editedSide === 'manuscript' ? 'new' : 'old';
  const untouchedParagraphs = paragraphs.map((paragraph) => [{ text: paragraph[plainText] ?? '', highlight: null }]);

  const editedSlot = {
    slideLabel: slideLabel(item[editedSide]),
    time: sideTime(item[editedSide]),
    ...(hasText(editedParagraphs) ? { paragraphs: editedParagraphs } : { text: CONTENT_NOT_PREVIEWABLE }),
  };
  const untouchedSide = editedSide === 'manuscript' ? 'studio' : 'manuscript';
  const untouchedSlot = {
    slideLabel: slideLabel(item[untouchedSide]),
    time: sideTime(item[untouchedSide]),
    ...(hasText(untouchedParagraphs) ? { paragraphs: untouchedParagraphs } : { text: CONTENT_NOT_PREVIEWABLE }),
  };

  return { [editedSide]: editedSlot, [untouchedSide]: untouchedSlot };
}

// 'conflict' means both sides diverged independently, so each renders its
// own real text (equal + its own segments) instead of one edited/one untouched.
function buildConflictSides(item) {
  const paragraphs = changedParagraphs(item.text_diff);
  const manuscriptParagraphs = paragraphs.map((paragraph) => (paragraph.segments ?? [])
    .filter((segment) => segment.op === 'equal' || segment.op === 'delete')
    .map((segment) => ({ text: segment.text, highlight: segment.op === 'delete' ? 'removed' : null })));
  const studioParagraphs = paragraphs.map((paragraph) => (paragraph.segments ?? [])
    .filter((segment) => segment.op === 'equal' || segment.op === 'insert')
    .map((segment) => ({ text: segment.text, highlight: segment.op === 'insert' ? 'added' : null })));

  return {
    manuscript: {
      slideLabel: slideLabel(item.manuscript),
      time: sideTime(item.manuscript),
      ...(hasText(manuscriptParagraphs) ? { paragraphs: manuscriptParagraphs } : { text: CONTENT_NOT_PREVIEWABLE }),
    },
    studio: {
      slideLabel: slideLabel(item.studio),
      time: sideTime(item.studio),
      ...(hasText(studioParagraphs) ? { paragraphs: studioParagraphs } : { text: CONTENT_NOT_PREVIEWABLE }),
    },
  };
}

function buildDiffedSides(item) {
  if (item.badge === REVIEW_CHANGE_BADGE_CONFLICT) return buildConflictSides(item);
  return buildEditedSides(item, item.badge === REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT ? 'manuscript' : 'studio');
}

// manuscript side is a curated { target_url, external_url }; Studio side is
// the module's raw options doc, where a URL only exists for options.action === 'link'.
function linkUrl(side, isStudio) {
  if (isStudio) {
    const options = side.content?.options ?? {};
    return options.action === 'link' ? (options.target ?? null) : null;
  }
  return side.content?.target_url || side.content?.external_url || null;
}

const CONTENT_TYPE_LABELS = {
  internal_asset: 'Asset',
  link: 'Link',
};

function humanize(value) {
  if (!value) return '';
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function contentTypeLabel(item) {
  if (!item.content_type) return 'Text';
  return CONTENT_TYPE_LABELS[item.content_type] || humanize(item.content_type);
}

function notPreviewableText(contentType, side) {
  const moduleType = moduleTypeToLabel(side?.content?.type);
  const detail = moduleType && moduleType !== contentType ? ` (${moduleType})` : '';
  return `${contentType} content${detail} isn't previewable here yet.`;
}

// Walks a JSON-stringified ProseMirror doc, joining leaf text/hard_break
// nodes into a plain string. Marks/attrs are dropped -- this is a read-only preview.
function extractProseMirrorText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hard_break') return '\n';
  if (!Array.isArray(node.content)) return '';
  const text = node.content.map(extractProseMirrorText).join('');
  return node.type === 'paragraph' ? `${text}\n` : text;
}

// manuscript side is a curated { text, text_type }; Studio side has no such field --
// its text (if any) lives at options.content, a ProseMirror JSON string, same as a
// module's own rendered content (see TextModule/AdvancedButtonModule).
function textFromContent(side, isStudio) {
  const raw = isStudio ? side.content?.options?.content : side.content?.text;
  if (!raw) return null;
  if (!isStudio && side.content?.text_type !== 'prosemirror') return raw;
  try {
    const text = extractProseMirrorText(JSON.parse(raw)).replace(/\n+$/, '');
    return text || null;
  } catch {
    return null;
  }
}

// assetLinkUrl mirrors the "Open Asset" link Studio's own MiniMenu builds.
// manuscript side is a curated { asset_id }; Studio side nests it under options,
// same as an asset module's own moduleDefaults shape (options.asset_id).
function buildNoDiffSide(side, isStudio, appDomain, contentType) {
  const assetId = isStudio ? side.content?.options?.asset_id : side.content?.asset_id;
  const url = linkUrl(side, isStudio);
  const text = !assetId && !url ? textFromContent(side, isStudio) : null;
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...(assetId && { assetId, ...(appDomain && { assetLinkUrl: `${appDomain}/player/${assetId}` }) }),
    ...(url && { linkUrl: url }),
    ...(text && { text }),
    ...(!assetId && !url && !text && { text: notPreviewableText(contentType, side) }),
  };
}

function buildAddedSides(item, appDomain) {
  const contentType = contentTypeLabel(item);
  return {
    manuscript: item.manuscript ? buildNoDiffSide(item.manuscript, false, appDomain, contentType) : null,
    studio: item.studio ? buildNoDiffSide(item.studio, true, appDomain, contentType) : null,
  };
}

const CHANGE_LABELS = {
  modified: 'Modified',
  add: 'Added',
  unmatched: 'Added',
  missing: 'Removed',
};

function changeLabel(item) {
  return CHANGE_LABELS[item.change] || humanize(item.change);
}

function changeTypeLabel(item) {
  return [contentTypeLabel(item), changeLabel(item)].filter(Boolean).join(' ');
}

function cardTitle(item) {
  const primary = item.section?.title || item.label || 'Untitled';
  const changeType = changeTypeLabel(item);
  const showLabel = !!(item.section?.title && item.label);
  const parts = [primary];
  if (changeType) parts.push(showLabel ? `${changeType}: ${item.label}` : changeType);
  return parts.join(' | ');
}

function navLabel(item) {
  const primary = item.section?.title || item.label || 'Untitled';
  const changeType = changeTypeLabel(item);
  const parts = [primary];
  if (changeType) parts.push(changeType);
  if (item.page?.order != null) parts.push(`Page: ${item.page.order}`);
  return parts.join(' | ');
}

/**
 * Maps the live GET /manuscripts/<id>/studio-diff response into what
 * ReviewChangeCard expects. Only items with `can_sync: true` and a badge the
 * panel recognizes are kept -- everything else (structural section/page
 * entries, content types Studio can't sync at all) is dropped.
 *
 * @param {object} diff - the studio-diff response body.
 * @param {string} [appDomain] - `domains.appDomain` from the store, used to
 *   build an internal_asset's "Open Asset" link. Omit to skip that link.
 */
export function mapStudioDiffToReviewChanges(diff, appDomain) {
  const changes = diff?.changes ?? [];
  return changes
    .filter((item) => item.can_sync && KNOWN_BADGES.has(item.badge))
    .map((item) => ({
      id: item.id,
      title: cardTitle(item),
      navLabel: navLabel(item),
      badge: item.badge,
      ...(item.text_diff ? buildDiffedSides(item) : buildAddedSides(item, appDomain)),
    }));
}

export default mapStudioDiffToReviewChanges;
